import React, { useEffect, useState } from 'react';
import { CheckIcon, CopyIcon, FileTextIcon, MailIcon, SendIcon } from 'lucide-react';
import { CompanySettings } from '../../types/pro';
import { Modal } from './Modal';
const field = 'w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
const label = 'mb-1.5 block text-sm font-medium text-stone-700';
const ghost = 'rounded-lg border border-stone-200 bg-white px-4 py-2.5 text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-100';
const primary = 'rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:bg-stone-200 disabled:text-stone-400';
interface SendDocumentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** "Faktura" or "Tilbud" — drives every label in the dialog. */
  kind: 'Faktura' | 'Tilbud';
  documentNo: string;
  recipientName: string;
  recipientEmail: string;
  /** Named contact, used for the greeting when we have one. */
  attention?: string;
  company: CompanySettings;
  /** Marks the document as sent in the app. */
  onSent: () => void;
  /** Opens the printable document so the user can save the PDF. */
  onOpenPreview: () => void;
}

/**
 * There is no mail server behind this app, so the honest thing to do is hand
 * the finished message to the mail program the user already sends from —
 * rather than pretending a "Send" button delivered it.
 */
export function SendDocumentDialog({
  isOpen,
  onClose,
  kind,
  documentNo,
  recipientName,
  recipientEmail,
  attention,
  company,
  onSent,
  onOpenPreview
}: SendDocumentDialogProps) {
  const firstName = (attention || recipientName || '').trim().split(' ')[0];
  const lower = kind.toLowerCase();
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    setTo(recipientEmail || '');
    setSubject(`${kind} ${documentNo} fra ${company.name}`);
    setMessage(`Hej${firstName ? ` ${firstName}` : ''}\n\n` + `Vedhæftet finder du ${lower} ${documentNo}.\n\n` + `Du er velkommen til at skrive eller ringe, hvis du har spørgsmål.\n\n` + `Med venlig hilsen\n${company.name}\n${company.phone}`);
    setCopied(false);
  }, [isOpen, kind, lower, documentNo, recipientEmail, company.name, company.phone, firstName]);
  const send = () => {
    const url = `mailto:${encodeURIComponent(to.trim())}` + `?subject=${encodeURIComponent(subject)}` + `&body=${encodeURIComponent(message)}`;
    window.location.href = url;
    onSent();
    onClose();
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${subject}\n\n${message}`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Send ${lower}`} subtitle={`${documentNo} til ${recipientName || 'kunden'}`} footer={<>
          <button onClick={onClose} className={ghost} data-id="element-614" data-mp="1hbrp7fh34okl">
            Annuller
          </button>
          <button onClick={() => {
      onSent();
      onClose();
    }} className={ghost} data-id="element-615" data-mp="1ce9noa5edbmm">
            Marker som sendt
          </button>
          <button onClick={send} disabled={!to.trim()} className={`inline-flex items-center gap-2 ${primary}`} data-id="element-616" data-mp="251vojan8q25s">
            <SendIcon className="h-4 w-4" data-id="element-617" data-mp="2092miuj5p7xe" />
            Åbn i mailprogram
          </button>
        </>} data-id="element-613" data-mp="1rbiixextmzjm">
      <div className="space-y-4" data-id="element-618" data-mp="2srq43zyy24tq">
        {/* The PDF has to be attached by hand, so say so before they send. */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3.5" data-id="element-619" data-mp="2o0hqerqkzxar">
          <FileTextIcon className="h-4 w-4 flex-shrink-0 text-amber-700" data-id="element-620" data-mp="28hymo9x2c3b6" />
          <p className="min-w-0 flex-1 text-sm text-amber-900" data-id="element-621" data-mp="2dhtfwk4b94ze">
            Gem {lower} som PDF først, så du kan vedhæfte den i mailen.
          </p>
          <button onClick={() => {
          onOpenPreview();
          onClose();
        }} className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100" data-id="element-622" data-mp="1ymdfzyh1uev3">
            Gem som PDF
          </button>
        </div>

        <label className="block" data-id="element-623" data-mp="2hjocxcf7kw7d">
          <span className={label} data-id="element-624" data-mp="2hin2na60odqp">Til</span>
          <input value={to} onChange={e => setTo(e.target.value)} inputMode="email" placeholder="kunde@eksempel.dk" className={field} data-id="element-625" data-mp="2hhlmira59124" />
          {!recipientEmail && <span className="mt-1 block text-xs text-stone-400" data-id="element-626" data-mp="3ezc0ksruivut">
              Kunden har ingen e-mail i kartoteket — skriv den her.
            </span>}
        </label>

        <label className="block" data-id="element-627" data-mp="yl9yw9n0gow2">
          <span className={label} data-id="element-628" data-mp="yjyymjsrxz9p">Emne</span>
          <input value={subject} onChange={e => setSubject(e.target.value)} className={field} data-id="element-629" data-mp="yj5wjw4rfxxw" />
        </label>

        <label className="block" data-id="element-630" data-mp="18c4krcwchmgz">
          <span className={label} data-id="element-631" data-mp="18d5y5o0u2l1v">Besked</span>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={8} className={`${field} leading-relaxed`} data-id="element-632" data-mp="kkfr9t7seryg" />
        </label>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-100 pt-3" data-id="element-633" data-mp="tmynvvkzc8c9">
          <p className="flex items-center gap-2 text-xs text-stone-500" data-id="element-634" data-mp="pjiv5ac16s4u">
            <MailIcon className="h-3.5 w-3.5" data-id="element-635" data-mp="250j848c2meed" />
            Mailen åbnes i dit eget mailprogram, klar til at sende.
          </p>
          <button onClick={copy} className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-stone-600 transition-colors hover:bg-stone-100" data-id="element-636" data-mp="20ypg0ofxyipt">
            {copied ? <>
                <CheckIcon className="h-4 w-4 text-emerald-600" data-id="element-637" data-mp="17n0roy6ksv6x" />
                Kopieret
              </> : <>
                <CopyIcon className="h-4 w-4" data-id="element-638" data-mp="1bn1jdkior2vx" />
                Kopier tekst
              </>}
          </button>
        </div>
      </div>
    </Modal>;
}